// RoleContext — caches whether the signed-in user is a business owner
// or a client. Mirrors iOS UserSessionManager.isOwner.
//
// Hydrates from localStorage immediately to avoid flash, then revalidates
// in the background by calling GET /me/profile. Subsequent renders read
// from context without re-fetching.
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useAuth } from "./AuthContext";
import ApiService from "../../services/api.service";

const RoleContext = createContext(null);

const CACHE_KEY = "polyp_role";

function readCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.isBusinessOwner !== "boolean") return null;
    if (typeof parsed.isAdmin !== "boolean") parsed.isAdmin = false;
    return parsed;
  } catch {
    return null;
  }
}

function writeCache(payload) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(payload));
  } catch {
    // ignore quota / privacy mode
  }
}

function clearCache() {
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch {}
}

export function RoleProvider({ children }) {
  const { user } = useAuth();
  const uid = user?.uid || null;

  // Only treat the cache as valid if it was written for the currently signed-in
  // user. Otherwise a previous user's role would flash before the API call.
  const cachedForUser = (() => {
    const c = readCache();
    if (!c || !uid) return null;
    return c.userId === uid ? c : null;
  })();

  const [role, setRole] = useState(cachedForUser);
  const [loading, setLoading] = useState(!cachedForUser);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    if (!user) {
      setRole(null);
      clearCache();
      return null;
    }
    setError(null);
    try {
      const profile = await ApiService.getMeProfile();
      const payload = {
        isBusinessOwner: !!profile?.stats?.isBusinessOwner,
        isAdmin: !!profile?.isAdmin,
        // Instagram lead-ad intake: when "awaiting_placement", RoleGate routes
        // the user to the "Finding Your Academy" mode instead of the portal.
        placementStatus: profile?.placementStatus || null,
        // Game-official accounts: RoleGate routes accountType === "official"
        // to the /official console; the page itself gates on officialStatus
        // (pending/approved/denied — approval is manual via the admin console).
        accountType: profile?.accountType || "client",
        officialStatus: profile?.officialStatus || null,
        // Coach accounts: RoleGate routes accountType === "coach" to /coach.
        // teamId/teamName are the FIRST of the coach's teams (league-assigned
        // — nil until a league admin creates one); teamCount is the total,
        // used by CoachSidebar to decide "Players" vs "Teams" nav wording
        // (2026-08-03, multi-team support). leagueId/leagueName are
        // league-assigned too. League accounts: RoleGate routes
        // accountType === "league" to /league; leagueId/leagueName are set
        // once via the app's "Name your league" gate.
        // Unified PW Manager (2026-09-24): what a staff account can do comes
        // from what it's connected to — the teams it coaches and the league
        // it owns — not from accountType (which stays a legacy value until the
        // backend backfill). /me returns both for every staff tier; the
        // fallbacks cover a cached payload from before the fields existed.
        teams: Array.isArray(profile?.teams) ? profile.teams : [],
        ownedLeague: profile?.ownedLeague || null,
        coachesTeam: Array.isArray(profile?.teams)
          ? profile.teams.length > 0
          : profile?.accountType === "coach" && !!profile?.teamId,
        ownsLeague: profile && "ownedLeague" in profile
          ? !!profile.ownedLeague
          : profile?.accountType === "league" && !!profile?.leagueId,
        teamId: profile?.teamId || null,
        teamName: profile?.teamName || null,
        teamCount: profile?.teamCount || 0,
        leagueId: profile?.leagueId || null,
        leagueName: profile?.leagueName || null,
        sport: profile?.sport || null,
        userId: profile?.userId || user.uid,
        firstName: profile?.firstName || "",
        lastName: profile?.lastName || "",
        email: profile?.email || user.email || "",
        phoneNumber: profile?.phoneNumber || "",
        initials: profile?.initials || null,
        profileColorId: profile?.profileColorId || null,
        business: profile?.business || null,
      };
      setRole(payload);
      writeCache(payload);
      return payload;
    } catch (err) {
      setError(err);
      return null;
    }
  }, [user]);

  useEffect(() => {
    let cancelled = false;
    if (!user) {
      setRole(null);
      setLoading(false);
      clearCache();
      return;
    }
    const cached = readCache();
    const cachedMatches = cached && cached.userId === user.uid;
    if (!cachedMatches) {
      // Different user (or no cache) — drop stale role and show loading
      // until /me/profile resolves.
      setRole(null);
      setLoading(true);
    }
    (async () => {
      await refresh();
      if (cancelled) return;
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [user, refresh]);

  return (
    <RoleContext.Provider value={{ role, loading, error, refresh }}>
      {children}
    </RoleContext.Provider>
  );
}

export function useRole() {
  const ctx = useContext(RoleContext);
  if (!ctx) {
    throw new Error("useRole must be used inside <RoleProvider>");
  }
  return ctx;
}
